import { Component, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'

class ErrorBoundary extends Component {
  state = { error: null, componentStack: null }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) {
    this.setState({ componentStack: info?.componentStack })
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-red-600 font-semibold">Something went wrong</p>
          <pre className="text-xs text-left bg-gray-100 p-4 rounded max-w-2xl w-full overflow-auto whitespace-pre-wrap">
            {this.state.error.message}{'\n\n'}
            {'Component stack:'}{'\n'}{this.state.componentStack}
          </pre>
          <button onClick={() => window.location.reload()} className="text-sm underline text-blue-600">Reload</button>
        </div>
      )
    }
    return this.props.children
  }
}
import Layout from './components/Layout'
import { inicioDe, entraAOt, entraAComercial, tieneFuncionComercial } from './components/espacios'
import { inicioComercial } from './components/comercial/comercialLogica'
import ComercialOrganizaciones from './pages/comercial/ComercialOrganizaciones'
import ComercialContactos from './pages/comercial/ComercialContactos'
import ComercialTipos from './pages/comercial/ComercialTipos'
import ComercialEtiquetas from './pages/comercial/ComercialEtiquetas'
import ComercialPermisos from './pages/comercial/ComercialPermisos'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Schedule from './pages/Schedule'
import ThisWeek from './pages/ThisWeek'
import PlanWeek from './pages/PlanWeek'
import Projects from './pages/Projects'
import TimeOff from './pages/TimeOff'
import Personas from './pages/Personas'
import GeneralManagement from './pages/GeneralManagement'
import ProjectSetup from './pages/ProjectSetup'
import SCurve from './pages/SCurve'
import Tasks from './pages/Tasks'
import ProjectDetail from './pages/ProjectDetail'
import Settings from './pages/Settings'
import ApiKeys from './pages/ApiKeys'
import Purchases from './pages/Purchases'
import PurchaseDetail from './pages/PurchaseDetail'
import PurchaseNew from './pages/PurchaseNew'
import PurchasesDashboard from './pages/PurchasesDashboard'
import PurchasePriorityQueue from './pages/PurchasePriorityQueue'
import PurchaseFlowGuide from './pages/PurchaseFlowGuide'
import ComprasPedidos from './pages/ComprasPedidos'
import ComprasBandeja from './pages/ComprasBandeja'
import CRM from './pages/CRM'
import CRMTasks from './pages/CRMTasks'
import CRMDashboard from './pages/CRMDashboard'
import CLevelDashboard from './pages/CLevelDashboard'
import Capacity from './pages/Capacity'
import Scenarios from './pages/Scenarios'
import Forbidden from './pages/Forbidden'
import RequestsPurchases from './pages/RequestsPurchases'
import Unidades from './pages/Unidades'
import Partes from './pages/Partes'
import Asignaciones from './pages/Asignaciones'
import AdminPersonas from './pages/admin/AdminPersonas'
import AdminAreas from './pages/admin/AdminAreas'
import AdminCargos from './pages/admin/AdminCargos'
import AdminCuentas from './pages/admin/AdminCuentas'
import AdminDuplicados from './pages/admin/AdminDuplicados'
import AdminGrupoPersonas from './pages/admin/AdminGrupoPersonas'
import AdminGrupoAreas from './pages/admin/AdminGrupoAreas'
import AdminCalendario from './pages/admin/AdminCalendario'
import AdminContratos from './pages/admin/AdminContratos'
import AdminProyectos from './pages/admin/AdminProyectos'
import { MANTENIMIENTO_VER } from './utils/mantenimiento'
import PlantaOT from './pages/PlantaOT'
import PlantaOTNueva from './pages/PlantaOTNueva'
import PlantaAdmision from './pages/PlantaAdmision'
import PlantaGrupos from './pages/PlantaGrupos'
import PlantaPermisos from './pages/PlantaPermisos'
import PlantaColaSap from './pages/PlantaColaSap'
import CargasMasivas from './pages/CargasMasivas'
import PlantaTrabajo from './pages/PlantaTrabajo'
import PlantaIndicadores from './pages/PlantaIndicadores'

// On starting the app (sign-in, or opening it at its root) a user who chose a
// start workspace lands there -- once: later visits to "/" are the dashboard.
let inicioPendiente = ['/', '/login'].includes(window.location.pathname)
function AlIniciar({ to, children }) {
  const ir = inicioPendiente && to && to !== '/'
  useEffect(() => { inicioPendiente = false }, [])
  return ir ? <Navigate to={to} replace /> : children
}

// Project setup moved into Administración; old links (with ?id=) keep working.
function ProjectSetupRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/admin/proyectos/configurar${search}`} replace />
}

function AppShell() {
  const { user, loading, isLoaded, isSignedIn } = useAuth()

  // Wait for MSAL to initialize and the local user lookup to settle
  if (!isLoaded || (isSignedIn && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        Loading…
      </div>
    )
  }

  if (!isSignedIn) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // Signed in via Entra but backend couldn't verify — show error instead of redirect loop
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center p-8">
        <p className="text-gray-700 font-medium">Unable to reach the server</p>
        <p className="text-sm text-gray-400">The backend may be starting up. Try refreshing in a moment.</p>
        <button onClick={() => window.location.reload()} className="mt-2 text-sm underline text-blue-600">
          Refresh
        </button>
      </div>
    )
  }

  const roles = user.roles || (user.role ? [user.role] : [])
  const isAdmin = roles.includes('admin')
  // Contracts (Administración > Contratos): admin and RRHH manage, c_level reads.
  const canContratos = ['admin', 'rrhh', 'c_level'].some(r => roles.includes(r))
  // People, accounts, areas and calendar: admin and RRHH (RRHH never touches admin accounts).
  const canGente = ['admin', 'rrhh'].some(r => roles.includes(r))

  // Engineering workspace = anyone with an engineering-domain role.
  const canEngineering = ['admin', 'pm_po', 'engineer'].some(r => roles.includes(r))
  const canAcquisitions = ['admin', 'pm_po', 'engineer', 'purchaser'].some(r => roles.includes(r))
  const canCRM = ['admin', 'commercial'].some(r => roles.includes(r))
  const canCLevel = ['admin', 'c_level'].some(r => roles.includes(r))
  // c_level EXACTLY -- mirrors auth_utils.require_exactly("c_level") on the
  // backend (routers/scenarios.py): an admin who is not ALSO c_level never
  // sees this, even though admin passes canCLevel above and can enter the
  // C-Level workspace generally.
  const canScenarios = roles.includes('c_level')
  const canPurchasesDashboard = ['admin', 'purchaser'].some(r => roles.includes(r))
  const canPurchasePriority = ['admin', 'purchaser', 'pm_po', 'c_level'].some(r => roles.includes(r))
  const canCRMDashboard = ['admin', 'commercial'].some(r => roles.includes(r))
  const canOperations = MANTENIMIENTO_VER.some(r => roles.includes(r)) || entraAOt(user)
  // Órdenes de trabajo (what the Kanban boards were): everyone in
  // Operaciones except a mantenimiento-only user. Each step inside is then
  // checked by OT function.
  const canPlanta = entraAOt(user)
  // Comercial (new module): by its own functions, never by role.
  const canComercial = entraAComercial(user)
  const canComercialVer = tieneFuncionComercial(user, 'comercial_ver')
  const canComercialConfigurar = tieneFuncionComercial(user, 'comercial_configurar')
  const canComercialPermisos = tieneFuncionComercial(user, 'comercial_permisos')
  // /comercial opens on the first page the user's functions allow (E4-2).
  const comercialHome = inicioComercial(user)

  // Where to send a user who hits "/" but doesn't belong in Engineering.
  const homeRedirect = canEngineering
    ? null
    : canCLevel ? '/c-level'
    : roles.includes('purchaser') ? '/purchases'
    : roles.includes('commercial') ? '/crm'
    : canPlanta ? '/operations/planta/ot'
    : canOperations ? '/operations/unidades'
    : canComercial ? '/comercial'
    : null  // no redirect target — they'll see Forbidden

  const guard = (allowed, element) => allowed ? element : <Forbidden />

  return (
    <Layout>
      <Routes>
        {/* Signed in but landed on /login (MSAL returns to the login-request URL
            after redirect) — send them to their home instead of Forbidden. */}
        <Route path="/login" element={<Navigate to={inicioDe(user, roles) || '/'} replace />} />

        {/* Engineering workspace */}
        <Route path="/" element={
          <AlIniciar to={inicioDe(user, roles)}>
            {homeRedirect ? <Navigate to={homeRedirect} replace /> : guard(canEngineering, <Dashboard />)}
          </AlIniciar>
        } />
        <Route path="/this-week"    element={guard(canEngineering, <ThisWeek />)} />
        <Route path="/schedule"     element={guard(canEngineering, <Schedule />)} />
        {/* People live in the one register: Personas (everyone), Administración (admin). */}
        <Route path="/team"         element={<Navigate to="/personas" replace />} />
        <Route path="/projects"     element={guard(canEngineering, <Projects />)} />
        <Route path="/projects/:id" element={guard(canEngineering, <ProjectDetail />)} />
        <Route path="/time-off"     element={guard(canEngineering, <TimeOff />)} />
        <Route path="/tasks"        element={guard(canEngineering, <Tasks />)} />
        <Route path="/requests/purchases" element={<RequestsPurchases />} />
        {/* Moved to Operaciones; the old address keeps working. */}
        <Route path="/unidades"           element={<Navigate to="/operations/unidades" replace />} />

        {/* Admin-only engineering pages, except /management (admin + c_level) */}
        <Route path="/plan"          element={guard(isAdmin, <PlanWeek />)} />
        <Route path="/management"    element={guard(canCLevel, <GeneralManagement />)} />
        <Route path="/status"        element={<Navigate to="/management" replace />} />
        <Route path="/project-setup" element={<ProjectSetupRedirect />} />
        <Route path="/s-curve"       element={guard(isAdmin, <SCurve />)} />
        <Route path="/settings"      element={guard(isAdmin, <Settings />)} />
        <Route path="/api-keys"      element={<ApiKeys />} />
        {/* The company directory and leave requests: everyone signed in. */}
        <Route path="/personas"      element={<Personas />} />
        <Route path="/billing/rates" element={<Navigate to="/s-curve#rates" replace />} />
        <Route path="/admin/users"   element={<Navigate to="/admin/cuentas" replace />} />

        {/* Administración workspace: people, accounts, areas, calendar and Contratos for admin and RRHH
            (Contratos also c_level); duplicates and projects are admin only. */}
        <Route path="/admin"            element={<Navigate to={canGente ? '/admin/personas' : '/admin/contratos'} replace />} />
        <Route path="/admin/personas"   element={guard(canGente, <AdminGrupoPersonas><AdminPersonas /></AdminGrupoPersonas>)} />
        <Route path="/admin/areas"      element={guard(canGente, <AdminGrupoAreas><AdminAreas /></AdminGrupoAreas>)} />
        <Route path="/admin/cargos"     element={guard(canGente, <AdminGrupoAreas><AdminCargos /></AdminGrupoAreas>)} />
        <Route path="/admin/proyectos"  element={guard(isAdmin, <AdminProyectos />)} />
        <Route path="/admin/proyectos/configurar" element={guard(isAdmin, <ProjectSetup />)} />
        <Route path="/admin/cuentas"    element={guard(canGente, <AdminGrupoPersonas><AdminCuentas /></AdminGrupoPersonas>)} />
        <Route path="/admin/duplicados" element={guard(isAdmin, <AdminGrupoPersonas><AdminDuplicados /></AdminGrupoPersonas>)} />
        <Route path="/admin/calendario" element={guard(canGente, <AdminCalendario />)} />
        <Route path="/admin/contratos"  element={guard(canContratos, <AdminContratos />)} />

        {/* Acquisitions workspace */}
        <Route path="/purchases"           element={guard(canAcquisitions, <Purchases />)} />
        <Route path="/purchases/dashboard" element={guard(canPurchasesDashboard, <PurchasesDashboard />)} />
        <Route path="/purchases/board"     element={<Navigate to="/purchases" replace />} />
        <Route path="/purchases/priority"  element={guard(canPurchasePriority, <PurchasePriorityQueue />)} />
        <Route path="/purchases/flow"      element={guard(canAcquisitions, <PurchaseFlowGuide />)} />
        <Route path="/compras/pedidos"     element={<ComprasPedidos />} />
        <Route path="/compras/bandeja"     element={guard(canPurchasesDashboard, <ComprasBandeja />)} />
        <Route path="/purchases/new"      element={<PurchaseNew />} />
        <Route path="/purchases/:id/edit" element={<PurchaseNew />} />
        <Route path="/purchases/:id"       element={<PurchaseDetail />} />

        {/* CRM workspace */}
        <Route path="/crm"           element={guard(canCRM, <CRM />)} />
        <Route path="/crm/dashboard" element={guard(canCRMDashboard, <CRMDashboard />)} />
        <Route path="/crm/tasks"     element={guard(canCRM, <CRMTasks />)} />

        {/* C-Level workspace */}
        <Route path="/c-level" element={guard(canCLevel, <CLevelDashboard />)} />
        <Route path="/capacity" element={guard(canCLevel, <Capacity />)} />
        <Route path="/scenarios" element={guard(canScenarios, <Scenarios />)} />

        {/* Comercial workspace (new CRM; the old one stays under /crm) */}
        <Route path="/comercial" element={canComercial && comercialHome ? <Navigate to={comercialHome} replace /> : <Forbidden />} />
        <Route path="/comercial/organizaciones" element={guard(canComercialVer, <ComercialOrganizaciones />)} />
        <Route path="/comercial/contactos" element={guard(canComercialVer, <ComercialContactos />)} />
        <Route path="/comercial/admin/tipos" element={guard(canComercialConfigurar, <ComercialTipos />)} />
        <Route path="/comercial/admin/etiquetas" element={guard(canComercialConfigurar, <ComercialEtiquetas />)} />
        <Route path="/comercial/admin/permisos" element={guard(canComercialPermisos, <ComercialPermisos />)} />

        {/* Operaciones workspace */}
        <Route path="/operations/planta/ot"      element={guard(canPlanta, <PlantaOT />)} />
        <Route path="/operations/planta/ot/nueva" element={guard(canPlanta, <PlantaOTNueva />)} />
        <Route path="/operations/planta/ot/servicio" element={<Navigate to="/operations/planta/ot/nueva?origen=servicio" replace />} />
        <Route path="/operations/planta/admision" element={guard(canPlanta, <PlantaAdmision />)} />
        <Route path="/operations/planta/cola-sap" element={guard(canPlanta, <PlantaColaSap />)} />
        <Route path="/operations/planta/grupos"   element={guard(canPlanta, <PlantaGrupos />)} />
        <Route path="/operations/planta/permisos" element={guard(canPlanta, <PlantaPermisos />)} />
        <Route path="/operations/planta/carga-masiva" element={<Navigate to="/operations/carga-masiva?tipo=ot" replace />} />
        <Route path="/operations/planta/migracion" element={<Navigate to="/operations/carga-masiva?tipo=ot" replace />} />
        <Route path="/operations/planta/trabajo" element={guard(canPlanta, <PlantaTrabajo />)} />
        <Route path="/operations/planta/indicadores" element={guard(canPlanta, <PlantaIndicadores />)} />
        <Route path="/operations/unidades"  element={guard(canOperations, <Unidades />)} />
        <Route path="/operations/partes"    element={guard(canOperations, <Partes />)} />
        <Route path="/operations/personas"  element={<Navigate to="/operations/asignaciones?vista=reportes" replace />} />
        <Route path="/operations/asignaciones" element={guard(canOperations, <Asignaciones />)} />
        <Route path="/operations/carga-masiva" element={guard(canOperations, <CargasMasivas />)} />
        {/* The old boards section (bodega, soldadura, alpha…) was removed from
            Operaciones; its addresses land on the workspace's start page. */}
        <Route path="/operations"           element={<Navigate to={canPlanta ? '/operations/planta/ot' : '/operations/unidades'} replace />} />
        <Route path="/operations/:board"    element={<Navigate to={canPlanta ? '/operations/planta/ot' : '/operations/unidades'} replace />} />

        <Route path="*" element={<Forbidden />} />
      </Routes>
    </Layout>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
